import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * PosterViewer — branded full-screen viewer for the BEATSEEK Founding 40 promo poster.
 * Linked from the founding 40 welcome email so recipients land on a styled experience
 * instead of a raw PNG.
 */
const PosterViewer = () => {
  const navigate = useNavigate();
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);

  // Toggle body class so global touch-action: pan-y can be relaxed to allow pinch-zoom
  useEffect(() => {
    document.body.classList.add('poster-viewer-active');
    return () => {
      document.body.classList.remove('poster-viewer-active');
    };
  }, []);

  return (
    <div
      data-testid="poster-viewer-page"
      style={{
        minHeight: '100vh',
        width: '100%',
        background: 'radial-gradient(circle at 50% 0%, #1a0d2e 0%, #0d1224 50%, #0a0e1a 100%)',
        color: '#e6e9f2',
        padding: '32px 16px 48px',
        boxSizing: 'border-box',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 18,
      }}
    >
      {/* Poster */}
      <div
        style={{
          marginTop: 0,
          width: '100%',
          maxWidth: 720,
          borderRadius: 16,
          overflow: 'hidden',
          border: '1px solid rgba(192,38,211,0.35)',
          boxShadow: '0 12px 48px rgba(255,0,0,0.25), 0 6px 24px rgba(192,38,211,0.25)',
          background: '#0d1224',
          aspectRatio: '4 / 5',
          position: 'relative',
        }}
        data-testid="poster-viewer-image-wrap"
      >
        {!loaded && !errored && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'rgba(255,255,255,0.5)',
              fontSize: '0.85rem',
              letterSpacing: '0.1em',
            }}
          >
            LOADING POSTER…
          </div>
        )}
        {errored ? (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 24,
              textAlign: 'center',
              color: 'rgba(255,255,255,0.78)',
            }}
          >
            <div style={{ fontSize: '2.5rem', marginBottom: 8 }}>🪩</div>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>Poster is on its way.</div>
            <div style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.6)', maxWidth: 360 }}>
              We&apos;re still polishing the master file. Hit reply on the welcome email and we&apos;ll send it directly.
            </div>
          </div>
        ) : (
          <img
            src="/founding-40-poster.png"
            alt="BEATSEEK Founding 40 Promo Poster"
            onLoad={() => setLoaded(true)}
            onError={() => setErrored(true)}
            data-testid="poster-viewer-image"
            style={{
              width: '100%',
              height: '100%',
              display: 'block',
              objectFit: 'cover',
              opacity: loaded ? 1 : 0,
              transition: 'opacity 400ms ease',
              touchAction: 'pinch-zoom',
            }}
          />
        )}
      </div>

      {/* Actions */}
      <div
        style={{
          display: 'flex',
          gap: 12,
          marginTop: 20,
          flexWrap: 'wrap',
          justifyContent: 'center',
        }}
      >
        <a
          href="/founding-40-poster.png"
          download="BEATSEEK_Founding_40_Poster.png"
          data-testid="poster-download-btn"
          style={{
            padding: '12px 26px',
            borderRadius: 10,
            background: 'linear-gradient(135deg, #ff0000 0%, #c026d3 50%, #06b6d4 100%)',
            color: '#fff',
            textDecoration: 'none',
            fontWeight: 800,
            letterSpacing: '0.1em',
            fontSize: '0.85rem',
            boxShadow: '0 6px 20px rgba(255,0,0,0.4)',
          }}
        >
          ⬇ DOWNLOAD POSTER
        </a>
        <button
          onClick={() => navigate('/early-access')}
          data-testid="poster-enter-btn"
          style={{
            padding: '12px 26px',
            borderRadius: 10,
            background: 'transparent',
            color: '#fff',
            border: '2px solid rgba(255,255,255,0.25)',
            fontWeight: 800,
            letterSpacing: '0.1em',
            fontSize: '0.85rem',
            cursor: 'pointer',
          }}
        >
          ▶ STEP INTO BEATSEEK
        </button>
      </div>

      <p
        style={{
          marginTop: 28,
          fontSize: '0.75rem',
          color: 'rgba(255,255,255,0.4)',
          letterSpacing: '0.08em',
          textAlign: 'center',
        }}
      >
        BEATSEEK · Founding 40 Edition
      </p>
    </div>
  );
};

export default PosterViewer;
